import { DestroyRef, Injectable, inject, signal, computed } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatSnackBar } from '@angular/material/snack-bar';
import { interval, of, from } from 'rxjs';
import { catchError, distinctUntilChanged, filter, switchMap } from 'rxjs/operators';
import { AppConfigService } from './config/app-config.service';
import { environment } from '@environments/environment';

interface ServerConfig {
  gitVersion?: string;
  displayVersion?: string;
  gitSha?: string;
  releaseVersion?: string;
  runtimeEnvironment?: string;
  [key: string]: any;
}

@Injectable({
  providedIn: 'root'
})
export class VersionCheckService {
  private readonly snackBar = inject(MatSnackBar);
  private readonly appConfig = inject(AppConfigService);
  private readonly destroyRef = inject(DestroyRef);

  private readonly checkInterval = 3 * 60 * 1000; // Check every 3 minutes

  // Modern Angular signals for reactive state
  private readonly _currentVersion = signal<string>('');
  private readonly _currentSha = signal<string>('');
  private readonly _isChecking = signal<boolean>(false);
  private readonly _lastCheckTime = signal<Date | null>(null);
  private readonly _environment = signal<string>('');

  // Public readonly signals
  readonly currentVersion = this._currentVersion.asReadonly();
  readonly currentSha = this._currentSha.asReadonly();
  readonly isChecking = this._isChecking.asReadonly();
  readonly lastCheckTime = this._lastCheckTime.asReadonly();
  readonly environment = this._environment.asReadonly();

  // Computed signal for version display
  readonly versionDisplay = computed(() => {
    const version = this._currentVersion();
    const sha = this._currentSha();
    const env = this._environment();
    const lastCheck = this._lastCheckTime();

    if (!version || version === 'CHANGEME') {
      return env === 'Local' ? 'Development' : 'Unknown';
    }

    let display = version;
    if (env && env !== 'Production') {
      display += ` (${env})`;
    }
    if (sha && sha !== 'CHANGEME') {
      display += ` [${sha.substring(0, 7)}]`;
    }
    if (lastCheck && this.shouldEnableVersionChecking()) {
      display += ` - checked: ${lastCheck.toLocaleTimeString()}`;
    }

    return display;
  });

  constructor() {
    // Initialize with current version from AppConfig
    const config = this.appConfig.config;
    this._currentVersion.set(config?.gitVersion || config?.displayVersion || '');
    this._currentSha.set(config?.gitSha || '');
    this._environment.set(config?.runtimeEnvironment || '');

    // Only initialize version checking in production environments
    // In local development, version info is static and checks are unnecessary
    if (this.shouldEnableVersionChecking()) {
      this.initVersionCheck();
    } else {
      console.log('ℹ️ Version checking disabled for local development');
    }
  }

  private shouldEnableVersionChecking(): boolean {
    const env = this._environment();
    const isProduction = !!environment.production;

    // Enable version checking if:
    // 1. We're in production mode, OR
    // 2. We have a non-local environment (Dev, Alpha, etc.)
    return isProduction || (env !== '' && env !== 'Local');
  }

  private initVersionCheck(): void {
    // Set up periodic checks using signals and modern RxJS patterns
    interval(this.checkInterval)
      .pipe(
        switchMap(() => this.checkVersion()),
        filter((hasUpdate): hasUpdate is boolean => typeof hasUpdate === 'boolean' && hasUpdate),
        distinctUntilChanged(),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(() => {
        this.showUpdateNotification();
      });
  }

  private checkVersion() {
    this._isChecking.set(true);

    // Use native fetch() like main.ts to avoid HTTP interceptors
    // Check against the same endpoints that main.ts uses for configuration
    const envConfigUrl = `${environment.envConfigFile}?nocache=${new Date().getTime()}`;

    return from(
      fetch(envConfigUrl, {
        cache: 'no-cache',
        headers: {
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          'Pragma': 'no-cache',
          'Expires': '0'
        }
      }).then(res => res.json())
    ).pipe(
      switchMap((serverConfig: ServerConfig) => {
        this._lastCheckTime.set(new Date());
        this._isChecking.set(false);

        const currentVer = this._currentVersion();
        const currentSha = this._currentSha();

        // Extract version info from the server config (same as main.ts pattern)
        const serverVersion = serverConfig.gitVersion || serverConfig.displayVersion || '';
        const serverSha = serverConfig.gitSha || '';
        const serverEnv = serverConfig.runtimeEnvironment || '';

        if (!currentVer) {
          // First time loading - set current version
          this._currentVersion.set(serverVersion);
          this._currentSha.set(serverSha);
          this._environment.set(serverEnv);
          return of(false);
        }

        // Check if version or SHA changed (more reliable than just version)
        const hasUpdate = currentVer !== serverVersion ||
                         (serverSha && currentSha !== serverSha);

        if (hasUpdate) {
          console.log(`🚀 New SyRF version detected!`);
          console.log(`  Current: ${currentVer} (${currentSha}) [${this._environment()}]`);
          console.log(`  Server:  ${serverVersion} (${serverSha}) [${serverEnv}]`);

          // Update the current version signals when we detect a new version
          this._currentVersion.set(serverVersion);
          this._currentSha.set(serverSha);
          this._environment.set(serverEnv);
        }

        return of(hasUpdate);
      }),
      catchError((error) => {
        console.warn('⚠️ Version check failed:', error);
        this._isChecking.set(false);
        this._lastCheckTime.set(new Date());
        return of(false);
      })
    );
  }

  private showUpdateNotification(): void {
    const snackBarRef = this.snackBar.open(
      '🚀 A new version of SyRF is available!',
      'RELOAD',
      {
        duration: 0, // Keep open until action
        panelClass: ['update-snackbar'],
        horizontalPosition: 'center',
        verticalPosition: 'bottom'
      }
    );

    snackBarRef.onAction().subscribe(() => {
      // Force a hard refresh to get the latest version
      window.location.reload();
    });
  }

  public forceCheck(): void {
    if (!this.shouldEnableVersionChecking()) {
      this.snackBar.open(
        'ℹ️ Version checking is disabled in local development',
        'DISMISS',
        { duration: 4000 }
      );
      return;
    }

    this.checkVersion().subscribe((hasUpdate) => {
      if (!hasUpdate) {
        this.snackBar.open(
          '✅ You are using the latest version',
          'DISMISS',
          { duration: 3000 }
        );
      }
    });
  }

  public getCurrentVersionInfo() {
    return {
      version: this._currentVersion(),
      sha: this._currentSha(),
      environment: this._environment(),
      displayVersion: this.appConfig.config?.displayVersion,
      lastCheck: this._lastCheckTime()
    };
  }
}
